import * as React from "react";
import { AnalyticsStore } from "bernie-core";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkCard, UitkCardContentSection, UitkCardLink } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkRating } from "uitk-react-rating";
import { UitkLayoutFlex } from "uitk-react-layout-flex";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { UitkExpandoLink, UitkExpandoLinkTypeSize } from "uitk-react-expando";
import { UitkPrimaryButton } from "uitk-react-button";
import { AnalyticsConfig } from "uitk-react-utils";

import PriceLockup from "components/flexComponents/Hotels/views/HotelsViews/components/shared/PriceLockup";
import ValueProposition from "components/flexComponents/Hotels/views/HotelsViews/components/shared/ValueProposition";
import DistanceOrRating from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DistanceOrRating";
import DescriptionOrReview from "components/flexComponents/Hotels/views/HotelsViews/components/shared/DescriptionOrReview";
import AmenitiesOrRecommendation from "components/flexComponents/Hotels/views/HotelsViews/components/shared/AmenitiesOrRecommendation";
import { getOptimizedImageUrl } from "src/components/flexComponents/Hotels/utils/hotelOptimizedImage";
import {
  expandoCollapsedTitle,
  expandoExpandedTitle,
} from "components/flexComponents/Hotels/views/HotelsViews/HotelUtils";
import { getHotelLink, getHotelLinkRel } from "components/flexComponents/Hotels/components/HotelCard";

import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ratingIcon } from "components/utility/RatingUtil";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { FlexClickTracker } from "components/utility/analytics/FlexClickTracker/FlexClickTracker";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { ExtendedContextStore } from "typings/flexFramework/FlexDefinitions";
import { Hotel, HotelsFlexModuleResult } from "typings/microserviceModels/hotels-flex-module";
import { withStores } from "stores";
import { HotelPriceLockup } from "src/components/flexComponents/Hotels/utils/A11y/HotelPriceLockup";

interface HotelImageCardProps {
  context: ExtendedContextStore;
  model: HotelsFlexModuleResult;
  hotel: Hotel;
  analytics: AnalyticsStore;
}

const HotelCard = withStores("analytics")((props: HotelImageCardProps) => {
  const { hotel, model, context } = props;
  const { callToAction, contentRecipe, destinationId } = model;
  const { hotelName, hotelStarRating, hotelFormattedFullAddress } = hotel;

  const hotelImageUrl = getOptimizedImageUrl(hotel, context);

  const { formatText } = useLocalization();

  /* covered in e2e test */
  /* istanbul ignore next */
  const expandoLinkAnalytics: AnalyticsConfig = {
    id: "LP.Hotels.HotelImageLeftSmall.ExpandoLink",
    callback: (theId, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: theId,
        action: Action.CLICK,
        linkName: description,
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const ButtonTracked = FlexClickTracker(UitkPrimaryButton);

  return (
    <UitkCard>
      <UitkCardContentSection padded={false}>
        <UitkCardContentSection>
          <UitkLayoutGrid columns={3} space="three">
            <UitkLayoutGridItem colSpan={1}>
              <UitkFigure ratio={UitkFigureAspectRatioType.R1_1} className="uitk-card-roundcorner-all">
                <LazyLoadedImage src={hotelImageUrl} alt={hotel.hotelName} context={context} />
              </UitkFigure>
            </UitkLayoutGridItem>

            <UitkLayoutGridItem colSpan={2}>
              <UitkLayoutFlex direction="column">
                <UitkHeading tag="h3" size={5} overflow="truncate-2-lines">
                  {hotelName}
                </UitkHeading>
                <UitkRating rating={hotelStarRating} icon={ratingIcon(context.locale)} className={context.site.brand} />
                <UitkText size={300}>{hotelFormattedFullAddress}</UitkText>
              </UitkLayoutFlex>
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
        </UitkCardContentSection>

        <UitkCardContentSection border="top">
          <UitkLayoutFlex justifyContent="space-between">
            <UitkLayoutFlex direction="column">
              <ValueProposition hotel={hotel} className="all-b-margin-four" />
              <DistanceOrRating model={model} hotel={hotel} context={context} />
            </UitkLayoutFlex>
            <UitkLayoutFlex className="uitk-type-right">
              <PriceLockup hotel={hotel} priceA11y={new HotelPriceLockup(hotel, formatText)} hasGetRatesText />
            </UitkLayoutFlex>
          </UitkLayoutFlex>
        </UitkCardContentSection>

        <UitkCardLink>
          <TrackedLink
            data-testid={`hotelcard-link-${hotel.hotelName}`}
            aria-label={hotel.hotelName}
            moduleName="hotels"
            rfrr="card"
            rel={getHotelLinkRel(callToAction)}
            href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
          />
        </UitkCardLink>
      </UitkCardContentSection>

      <UitkCardContentSection border="top">
        <UitkExpandoLink
          type={UitkExpandoLinkTypeSize.TYPE_200}
          isVisible={false}
          expandoTitle={expandoCollapsedTitle(contentRecipe)}
          expandedLabel={expandoExpandedTitle(contentRecipe)}
          analytics={expandoLinkAnalytics}
        >
          <DescriptionOrReview model={model} hotel={hotel} className="all-t-margin-three" />
          <AmenitiesOrRecommendation model={model} hotel={hotel} className="all-t-margin-three" />
          <ButtonTracked
            href={getHotelLink(hotel, destinationId || context.searchContext.location?.id, context, callToAction)}
            isFullWidth
            className="all-t-margin-three"
            moduleName="LP.hotels.hotelImageLeftSmall"
            rfrr="expandoExpanded.select"
          >
            {formatText("hotelsViews.expando.select")}
          </ButtonTracked>
        </UitkExpandoLink>
      </UitkCardContentSection>
    </UitkCard>
  );
});

export default HotelCard;
